/**
 * Riders, their rates, and the names nobody has mapped.
 *
 * The unmapped queue is the part that matters. A rider name CenoX has not been
 * told about still gets its rides counted, but accrues no remittance — so the
 * queue is money waiting on a decision, and it says so rather than sitting in a
 * corner as a tidy-up task.
 *
 * Nothing here suggests a match. Two spellings differing by a letter can be two
 * people, and the only safe reading of a near-miss is that somebody has to look.
 */

import type { ReactNode } from 'react';
import { Badge, Card, EmptyState, ErrorState, TableSkeleton } from '../../components/ui';
import { useAuth } from '../../lib/auth';
import { money } from '../../lib/format';
import { SectionTitle, seenRange } from './parts';
import { useRates, useRiders, useUnresolvedRiders } from './data';

export function LogisticsRidersPage(): ReactNode {
  const { business } = useAuth();
  const currency = business?.currencyCode ?? 'NGN';

  const riders = useRiders();
  const unresolved = useUnresolvedRiders();
  const rates = useRates();

  if (riders.isError) {
    return <ErrorState message="Could not load riders." onRetry={() => void riders.refetch()} />;
  }

  return (
    <div className="space-y-5">
      {(unresolved.data?.data.length ?? 0) > 0 ? (
        <section>
          <SectionTitle>Names waiting on a decision</SectionTitle>
          <Card>
            <p className="mb-2 text-xs text-slate-500">
              These rides are counted and reported. They accrue no remittance until the name is
              mapped to a rider, and nothing here guesses which.
            </p>
            <div className="-mx-3 overflow-x-auto px-3">
              <table className="w-full min-w-[480px] text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                    <th className="py-2 pr-3 font-medium">As recorded</th>
                    <th className="py-2 pr-3 text-right font-medium">Rides</th>
                    <th className="py-2 pr-3 text-right font-medium">Gross</th>
                    <th className="py-2 font-medium">Seen</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {unresolved.data!.data.map((u) => (
                    <tr key={u.riderName}>
                      <td className="py-2 pr-3 font-medium text-slate-900">{u.riderName}</td>
                      <td className="py-2 pr-3 text-right tabular-nums text-slate-600">{u.rides}</td>
                      <td className="py-2 pr-3 text-right tabular-nums text-slate-900">
                        {money(u.gross, currency)}
                      </td>
                      <td className="py-2 text-xs text-slate-500">
                        {seenRange(u.firstSeen, u.lastSeen)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </section>
      ) : null}

      <section>
        <SectionTitle>Riders</SectionTitle>
        {riders.isPending ? (
          <TableSkeleton />
        ) : (riders.data?.data.length ?? 0) === 0 ? (
          <EmptyState
            title="No riders yet"
            description="A rider is created once, and the names a sheet spells differently are mapped to it as approved aliases."
          />
        ) : (
          <Card>
            <div className="-mx-3 overflow-x-auto px-3">
              <table className="w-full min-w-[520px] text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                    <th className="py-2 pr-3 font-medium">Rider</th>
                    <th className="py-2 pr-3 font-medium">Phone</th>
                    <th className="py-2 pr-3 font-medium">Also written</th>
                    <th className="py-2 pr-3 font-medium">Remittance</th>
                    <th className="py-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {riders.data!.data.map((r) => (
                    <tr key={r.id}>
                      <td className="py-2 pr-3 font-medium text-slate-900">{r.canonicalName}</td>
                      <td className="py-2 pr-3 tabular-nums text-slate-600">{r.phone ?? '—'}</td>
                      <td className="py-2 pr-3 text-slate-500">
                        {r.aliases.length ? r.aliases.join(', ') : '—'}
                      </td>
                      <td className="py-2 pr-3">
                        {r.remittanceExempt ? (
                          <Badge tone="neutral">Exempt</Badge>
                        ) : (
                          <span className="text-slate-600">Charged</span>
                        )}
                      </td>
                      <td className="py-2">
                        <Badge tone={r.status === 'active' ? 'success' : 'neutral'}>{r.status}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </section>

      <section>
        <SectionTitle>Commission rates</SectionTitle>
        {(rates.data?.data.length ?? 0) === 0 ? (
          <EmptyState title="No rates configured" />
        ) : (
          <Card>
            <p className="mb-2 text-xs text-slate-500">
              Most specific wins: a rider&rsquo;s own rate, then a channel rule, then the workspace
              default. A rate that has ended keeps costing the deliveries it covered.
            </p>
            <div className="-mx-3 overflow-x-auto px-3">
              <table className="w-full min-w-[560px] text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                    <th className="py-2 pr-3 font-medium">Applies to</th>
                    <th className="py-2 pr-3 font-medium">Channel</th>
                    <th className="py-2 pr-3 text-right font-medium">Rate</th>
                    <th className="py-2 pr-3 font-medium">From</th>
                    <th className="py-2 font-medium">To</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rates.data!.data.map((rate) => (
                    <tr key={rate.id}>
                      <td className="py-2 pr-3 text-slate-900">
                        {rate.rider ?? <span className="text-slate-500">Workspace default</span>}
                      </td>
                      <td className="py-2 pr-3 text-slate-600">{rate.channel ?? 'All'}</td>
                      <td className="py-2 pr-3 text-right tabular-nums text-slate-900">
                        {rate.rateType === 'percentage'
                          ? `${Number(rate.rateValue)}%`
                          : money(rate.rateValue, currency)}
                      </td>
                      <td className="py-2 pr-3 tabular-nums text-slate-600">{rate.effectiveFrom}</td>
                      <td className="py-2 tabular-nums text-slate-600">
                        {rate.effectiveTo ?? <span className="text-emerald-700">current</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </section>
    </div>
  );
}
